'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import type { HeroNameAsk } from '@/contexts/characters/actions/heroNamesForLanguage';

/**
 * "This tale is in Czech — how is Мира written?"
 *
 * Asked once per hero per language, right before the story is created, and only
 * when the hero's name is not in the letters that language uses. The field is
 * prefilled with a transliteration (or the template's own translation), because
 * guessing is ours to do and deciding is the parent's: "Энжи" may be Enži,
 * Enzhi or Angie, and only they know which child that is.
 *
 * Keeping the original is a legitimate answer — the button says so.
 */
export interface HeroNamesDialogProps {
  open: boolean;
  asks: HeroNameAsk[];
  languageLabel: string;
  busy?: boolean;
  onCancel: () => void;
  /** name per hero, keyed by characterId when there is one, else by the original name. */
  onConfirm: (names: Record<string, string>) => void;
}

export function HeroNamesDialog({
  open, asks, languageLabel, busy, onCancel, onConfirm,
}: HeroNamesDialogProps) {
  const t = useTranslations('content.wizard.heroNames');
  const keyOf = (ask: HeroNameAsk) => ask.characterId ?? ask.original;
  const [values, setValues] = useState<Record<string, string>>({});

  // A fresh set of asks (another language, another cast) resets the fields.
  useEffect(() => {
    setValues(Object.fromEntries(asks.map((a) => [keyOf(a), a.suggestion || a.original])));
  }, [asks]);

  const submit = (useOriginals: boolean) => {
    onConfirm(
      Object.fromEntries(
        asks.map((a) => [keyOf(a), useOriginals ? a.original : (values[keyOf(a)] || a.original).trim()]),
      ),
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="hero-names-dialog">
        <DialogTitle>{t('title', { language: languageLabel })}</DialogTitle>
        <DialogDescription>{t('subtitle')}</DialogDescription>

        <div className="space-y-3 py-2">
          {asks.map((ask) => {
            const key = keyOf(ask);
            return (
              <div key={key} className="space-y-1">
                <Label htmlFor={`hero-name-${key}`} className="text-sm text-muted-foreground">
                  {ask.original}
                </Label>
                <Input
                  id={`hero-name-${key}`}
                  data-testid={`hero-name-input-${key}`}
                  value={values[key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  autoComplete="off"
                />
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={() => submit(true)} disabled={busy} data-testid="hero-names-keep">
            {t('keepOriginal')}
          </Button>
          <Button onClick={() => submit(false)} disabled={busy} data-testid="hero-names-confirm">
            {t('confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
